import { cn } from '@src/lib/utils';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useEffect,
} from 'react';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

/**
 * Minimal modal dialog (backdrop + centered panel), closing on backdrop click or Escape.
 * No existing Dialog primitive was available in `components/ui` yet (see #43) and no new
 * dependency (e.g. Radix) was pulled in to build one -- this follows the same
 * forwardRef + cn() convention as the other primitives in this folder.
 */
const Dialog = forwardRef<HTMLDivElement, DialogProps>(({ open, onClose, className, children }, ref) => {
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50" onClick={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        onClick={(event: MouseEvent) => event.stopPropagation()}
        className={cn(
          'flex max-h-[70vh] w-[540px] max-w-[90vw] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
});
Dialog.displayName = 'Dialog';

const DialogHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-between gap-3 border-b border-border px-5 py-3.5', className)}
      {...props}
    />
  ),
);
DialogHeader.displayName = 'DialogHeader';

const DialogTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2 ref={ref} className={cn('text-[13px] font-semibold text-foreground', className)} {...props} />
  ),
);
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('font-mono text-[11px] text-muted-foreground', className)} {...props} />
  ),
);
DialogDescription.displayName = 'DialogDescription';

const DialogBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-col gap-2 overflow-y-auto px-5 py-4', className)} {...props} />
));
DialogBody.displayName = 'DialogBody';

const DialogCloseButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label="Close"
      className={cn(
        'rounded p-1 text-lg leading-none text-muted-foreground transition-colors hover:text-foreground',
        className,
      )}
      {...props}
    >
      &times;
    </button>
  ),
);
DialogCloseButton.displayName = 'DialogCloseButton';

export { Dialog, DialogBody, DialogCloseButton, DialogDescription, DialogHeader, DialogTitle };
